import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";

// Typed union of valid lazyLoad keys — prevents TypeScript from needing to
// compute the full ParseKeys<'common',...> union when checking t() calls.
// String-typed messageKey causes a TS compiler crash (Debug Failure) because
// `string extends ParseKeys<'common'>` exhausts instantiation depth with no diagnostic.
type LazyLoadKey =
  | "lazyLoad.default"
  | "lazyLoad.dashboard"
  | "lazyLoad.events"
  | "lazyLoad.analytics"
  | "lazyLoad.documents"
  | "lazyLoad.marketplace"
  | "lazyLoad.finance"
  | "lazyLoad.tasks"
  | "lazyLoad.community"
  | "lazyLoad.referral"
  | "lazyLoad.settings"
  | "lazyLoad.profile"
  | "lazyLoad.users"
  | "lazyLoad.files"
  | "lazyLoad.badges"
  | "lazyLoad.surveys"
  | "lazyLoad.admin"
  | "lazyLoad.auth";

const LoadingFallback: React.FC<{ messageKey?: LazyLoadKey }> = ({
  messageKey,
}) => {
  const { t } = useTranslation("common");
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="200px"
      gap={2}
    >
      <CircularProgress size={40} />
      <Typography variant="body2" color="text.secondary">
        {t(messageKey ?? "lazyLoad.default")}
      </Typography>
    </Box>
  );
};

// HOC для lazy loading с error boundary
export const withLazyLoading = <P extends {}>(
  LazyComponent: React.LazyExoticComponent<React.ComponentType<P>>,
  messageKey?: LazyLoadKey,
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense
      fallback={
        <LoadingFallback
          {...(messageKey !== undefined ? { messageKey } : {})}
        />
      }
    >
      <LazyComponent {...(props as any)} />
    </Suspense>
  );
  return WrappedComponent;
};

// ========= DASHBOARD & MAIN PAGES =========
export const LazyEnhancedDashboard = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "dashboard" */ "../pages/enhanced/EnhancedDashboard"
      ),
  ),
  "lazyLoad.dashboard",
);

export const LazyMobileDashboardPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "mobile" */ "../pages/mobile/MobileDashboardPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= EVENTS =========
export const LazyEventList = withLazyLoading(
  React.lazy(
    () => import(/* webpackChunkName: "events" */ "../pages/events/EventList"),
  ),
  "lazyLoad.events",
);

export const LazyCreateEventPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/CreateEventPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventDetailsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventDetailsPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventListPage = withLazyLoading(
  React.lazy(
    () => import(/* webpackChunkName: "events" */ "../pages/events/EventList"),
  ),
  "lazyLoad.events",
);

export const LazyEventEditPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "events" */ "../pages/events/EditEventPage"),
  ),
  "lazyLoad.events",
);

export const LazyEventCalendarPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventCalendarPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventManagementPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventManagementPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventParticipantsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventParticipantsPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyLiveEventPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "events" */ "../pages/events/LiveEventPage"),
  ),
  "lazyLoad.events",
);

export const LazyEventAgendaPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventAgendaPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventBudgetPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventBudgetPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventQRPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "events" */ "../pages/events/EventQRPage"),
  ),
  "lazyLoad.events",
);

export const LazyEventAttendancePage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventAttendancePage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventResourcesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventResourcesPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventGuestJoinPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventGuestJoinPage"
      ),
  ),
  "lazyLoad.events",
);

/** AD-460. «Мой QR» — код участника, показанный самому участнику. */
export const LazyMyEventQRPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "events" */ "../pages/events/MyEventQRPage"),
  ),
  "lazyLoad.events",
);

export const LazyPublicEventRegistrationPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events-public" */ "../pages/events/PublicEventRegistrationPage"
      ),
  ),
  "lazyLoad.events",
);

// ========= SURVEYS =========
export const LazySurveyBuilderPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "surveys-builder" */ "../pages/surveys/SurveyBuilderPage"
      ),
  ),
  "lazyLoad.surveys",
);

export const LazySurveyRespondPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "surveys-respond" */ "../pages/surveys/SurveyRespondPage"
      ),
  ),
  "lazyLoad.surveys",
);

export const LazySurveyResultsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "surveys-results" */ "../pages/surveys/SurveyResultsPage"
      ),
  ),
  "lazyLoad.surveys",
);

// ========= ANALYTICS =========
export const LazySupplierAnalyticsDashboard = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics" */ "../pages/SupplierAnalyticsDashboard"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyAnalyticsDashboard = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics" */ "../pages/analytics/AnalyticsDashboard"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics" */ "../pages/analytics/AnalyticsPage"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyDashboardBuilderPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "dashboard" */ "../pages/dashboard/DashboardBuilderPage"
      ),
  ),
  "lazyLoad.dashboard",
);

export const LazyEventsAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics-events" */ "../pages/analytics/EventsAnalyticsPage"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyParticipantsAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics-participants" */ "../pages/analytics/ParticipantsAnalyticsPage"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyUsersAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics-users" */ "../pages/analytics/UsersAnalyticsPage"
      ),
  ),
  "lazyLoad.analytics",
);

export const LazyConversionsAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "analytics-conversions" */ "../pages/analytics/ConversionsAnalyticsPage"
      ),
  ),
  "lazyLoad.analytics",
);

// ========= DOCUMENTS =========
export const LazyDocumentsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents" */ "../pages/documents/DocumentsPage"
      ),
  ),
  "lazyLoad.documents",
);

export const LazyContractsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents-contracts" */ "../pages/documents/ContractsPage"
      ),
  ),
  "lazyLoad.documents",
);

export const LazyDocumentInvoicesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents-invoices" */ "../pages/documents/DocumentInvoicesPage"
      ),
  ),
  "lazyLoad.documents",
);

export const LazyDocumentReportsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents-reports" */ "../pages/documents/DocumentReportsPage"
      ),
  ),
  "lazyLoad.documents",
);

export const LazyTemplatesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents-templates" */ "../pages/documents/TemplatesPage"
      ),
  ),
  "lazyLoad.documents",
);

export const LazyDocumentEditorPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "documents-editor" */ "../pages/documents/DocumentEditorPage"
      ),
  ),
  "lazyLoad.documents",
);

// ========= MARKETPLACE =========
export const LazyMarketplacePage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/MarketplacePage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyQuotationPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/QuotationPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyMarketplaceSearchPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/MarketplaceSearchPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyMarketplaceComparePage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/MarketplaceComparePage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyMarketplaceFavoritesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/MarketplaceFavoritesPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazySupplierDetailsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/SupplierDetailsPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazySupplierHubPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/SupplierHubPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyServiceDetailsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/ServiceDetailsPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyMarketplaceServicesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/MarketplaceServicesPage"
      ),
  ),
  "lazyLoad.marketplace",
);

export const LazyOrderDetailPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "marketplace" */ "../pages/marketplace/OrderDetailPage"
      ),
  ),
  "lazyLoad.marketplace",
);

// ========= COMMUNICATIONS =========
export const LazyCommunicationsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "communications" */ "../pages/communications/CommunicationsPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazyChatPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "communications" */ "../pages/communications/ChatPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazyVideoCallPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "communications" */ "../pages/communications/VideoCallPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazyCommunityPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "community" */ "../pages/community/CommunityPage"
      ),
  ),
  "lazyLoad.community",
);

export const LazyCollaborationPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "collaboration" */ "../pages/collaboration/CollaborationPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= REFERRAL =========
export const LazyReferralPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/ReferralPage"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralDashboardPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/ReferralDashboard"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralEarningsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/EarningsPage"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralNetworkPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/ReferralNetworkPage"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralLeaderboardPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/LeaderboardPage"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralSettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/ReferralSettings"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyReferralInvitePage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/ReferralInvitePage"
      ),
  ),
  "lazyLoad.referral",
);

export const LazyAgentPayoutsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "referral" */ "../pages/referral/AgentPayoutsPage"
      ),
  ),
  "lazyLoad.referral",
);

// ========= SUBSCRIPTIONS =========
export const LazySubscriptionPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "subscription" */ "../pages/subscription/SubscriptionPage"
      ),
  ),
  "lazyLoad.default",
);

// Keep legacy lazy wrappers so App.tsx redirect entries still compile
export const LazyPricingPlansPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "subscription-plans" */ "../pages/subscription/PricingPlansPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazySubscriptionManagementPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "subscription-manage" */ "../pages/subscription/SubscriptionManagementPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazyAiPackagesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "subscription-ai" */ "../pages/subscription/AiPackagesPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= FINANCE =========
export const LazyFinancePage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "finance" */ "../pages/finance/FinancePage"),
  ),
  "lazyLoad.finance",
);

export const LazyBudgetPlannerPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "finance" */ "../pages/finance/BudgetPlannerPage"
      ),
  ),
  "lazyLoad.finance",
);

export const LazyPaymentsPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "finance" */ "../pages/finance/PaymentsPage"),
  ),
  "lazyLoad.finance",
);

export const LazyReportsPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "finance" */ "../pages/finance/ReportsPage"),
  ),
  "lazyLoad.finance",
);

export const LazyReportTemplatesPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "finance" */ "../pages/finance/ReportTemplatesPage"
      ),
  ),
  "lazyLoad.finance",
);

export const LazyInvoicesPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "finance" */ "../pages/finance/InvoicesPage"),
  ),
  "lazyLoad.finance",
);

export const LazyFinancialAnalyticsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "finance" */ "../pages/finance/FinancialAnalyticsPage"
      ),
  ),
  "lazyLoad.finance",
);

export const LazyTransactionsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "finance" */ "../pages/finance/TransactionsPage"
      ),
  ),
  "lazyLoad.finance",
);

export const LazyCommissionsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "finance" */ "../pages/finance/CommissionsPage"
      ),
  ),
  "lazyLoad.finance",
);

// ========= TEAM & COLLABORATION =========
export const LazyTeamPage = withLazyLoading(
  React.lazy(
    () => import(/* webpackChunkName: "team" */ "../pages/team/TeamPage"),
  ),
  "lazyLoad.default",
);

export const LazyNotificationsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "notifications" */ "../pages/notifications/NotificationsPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazyPartnersPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "partners" */ "../pages/partners/PartnersPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= UTILITY COMPONENTS =========
export const LazyErrorBoundary = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "utils" */ "../components/system/ErrorBoundary"
      ),
  ),
  "lazyLoad.default",
);

export const LazyLoadingStates = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "utils" */ "../components/system/LoadingStates"
      ),
  ),
  "lazyLoad.default",
);

export const LazyOfflineMode = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "utils" */ "../components/system/OfflineMode"
      ),
  ),
  "lazyLoad.default",
);

// ========= ADDITIONAL PAGES =========
export const LazyPersonalizationPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/personalization/PersonalizationPage"
      ),
  ),
  "lazyLoad.default",
);

export const LazySettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "pages" */ "../pages/settings/SettingsPage"),
  ),
  "lazyLoad.settings",
);

export const LazyNotificationSettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/NotificationSettingsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazySecuritySettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/SecuritySettingsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyProfilePage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "pages" */ "../pages/settings/ProfilePage"),
  ),
  "lazyLoad.profile",
);

export const LazyPrivacySettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/PrivacySettingsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyTaxInformationPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/TaxInformationPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyCrmIntegrationsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/CrmIntegrationsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyWebhooksSettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "pages" */ "../pages/settings/WebhooksSettingsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyApiKeysPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "pages" */ "../pages/settings/ApiKeysPage"),
  ),
  "lazyLoad.settings",
);

export const LazyUsersListPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "users" */ "../pages/users/UsersListPage"),
  ),
  "lazyLoad.users",
);

export const LazyUserCreatePage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "users" */ "../pages/users/UserCreatePage"),
  ),
  "lazyLoad.users",
);

export const LazyUserDetailPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "users" */ "../pages/users/UserDetailPage"),
  ),
  "lazyLoad.users",
);

// ========= MAIN TASKS =========
export const LazyTasksPage = withLazyLoading(
  React.lazy(
    () => import(/* webpackChunkName: "tasks" */ "../pages/tasks/TasksPage"),
  ),
  "lazyLoad.tasks",
);

export const LazyTaskCreatePage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "tasks" */ "../pages/tasks/TaskCreatePage"),
  ),
  "lazyLoad.tasks",
);

// ========= EVENT TASKS =========
export const LazyEventTasksPage = withLazyLoading(
  React.lazy(
    () =>
      import(/* webpackChunkName: "events" */ "../pages/events/EventTasksPage"),
  ),
  "lazyLoad.tasks",
);

export const LazyEventTaskCreatePage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventTaskCreatePage"
      ),
  ),
  "lazyLoad.tasks",
);

export const LazyEventTaskDetailPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventTaskDetailPage"
      ),
  ),
  "lazyLoad.tasks",
);

export const LazyEventTaskEditPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventTaskEditPage"
      ),
  ),
  "lazyLoad.tasks",
);

// ========= ADDITIONAL EVENTS =========
export const LazyEventPopularPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventPopularPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyEventOverviewPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "events" */ "../pages/events/EventOverviewPage"
      ),
  ),
  "lazyLoad.events",
);

// ========= ORDERS =========
export const LazyOrdersPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "orders" */ "../pages/marketplace/OrdersPage"
      ),
  ),
  "lazyLoad.marketplace",
);

// ========= MONITORING =========
export const LazyMonitoringPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "monitoring" */ "../pages/monitoring/MonitoringPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= ADMIN USERS =========
export const LazyAdminUsersPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-users" */ "../pages/admin/AdminUsersPage"
      ),
  ),
  "lazyLoad.users",
);

// ========= ADMIN REFERRALS =========
export const LazyAdminReferralsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-referrals" */ "../pages/admin/AdminReferralsPage"
      ),
  ),
  "lazyLoad.admin",
);

// ========= ADMIN SYSTEM =========
export const LazyAdminSystemPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-system" */ "../pages/admin/AdminSystemPage"
      ),
  ),
  "lazyLoad.admin",
);

// ========= ADMIN SECURITY =========
export const LazyAdminSecurityPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-security" */ "../pages/admin/AdminSecurityPage"
      ),
  ),
  "lazyLoad.admin",
);

// ========= ADMIN BACKUP =========
export const LazyAdminBackupPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-backup" */ "../pages/admin/AdminBackupPage"
      ),
  ),
  "lazyLoad.admin",
);

// ========= ADMIN PANEL (NEW) =========
export const LazyAdminDashboard = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-dashboard" */ "../pages/admin/AdminDashboard"
      ),
  ),
  "lazyLoad.dashboard",
);

export const LazyAdminUsers = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-users-new" */ "../pages/admin/AdminUsers"
      ),
  ),
  "lazyLoad.users",
);

export const LazyAdminModeration = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-moderation" */ "../pages/admin/AdminModeration"
      ),
  ),
  "lazyLoad.admin",
);

export const LazyAdminSettings = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-settings" */ "../pages/admin/AdminSettings"
      ),
  ),
  "lazyLoad.admin",
);

export const LazyAdminReports = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "admin-reports" */ "../pages/admin/AdminReports"
      ),
  ),
  "lazyLoad.admin",
);

// ========= FILES =========
export const LazyFilesPage = withLazyLoading(
  React.lazy(
    () => import(/* webpackChunkName: "files" */ "../pages/FilesPage"),
  ),
  "lazyLoad.files",
);

// ========= SUPPLIER CATALOG =========
export const LazySupplierCatalogPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "supplier-catalog" */ "../pages/supplier/SupplierCatalogPage"
      ),
  ),
  "lazyLoad.default",
);

// ========= SPEAKER STATS =========
export const LazySpeakerStatsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "speaker-stats" */ "../pages/analytics/SpeakerStatsPage"
      ),
  ),
  "lazyLoad.analytics",
);

// ========= BADGES =========
export const LazyBadgeDesignerPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "badges-designer" */ "../pages/events/BadgeDesignerPage"
      ),
  ),
  "lazyLoad.badges",
);

export const LazyBadgePrintPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "badges-print" */ "../pages/events/BadgePrintPage"
      ),
  ),
  "lazyLoad.badges",
);

export const LazyBadgeKioskPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "badges-kiosk" */ "../pages/events/BadgeKioskPage"
      ),
  ),
  "lazyLoad.badges",
);

export const LazySsoCallbackPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "sso-callback" */ "../pages/auth/SsoCallbackPage"
      ),
  ),
  "lazyLoad.auth",
);

export const LazySsoSettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "sso-settings" */ "../pages/settings/SsoSettingsPage"
      ),
  ),
  "lazyLoad.settings",
);

export const LazyEventLeaderboardPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "gamification-leaderboard" */ "../pages/events/EventLeaderboardPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyGamificationSettingsPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "gamification-settings" */ "../pages/events/GamificationSettingsPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyExhibitorDashboardPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "exhibitor-dashboard" */ "../pages/lead-retrieval/ExhibitorDashboardPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyLeadScannerPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "lead-scanner" */ "../pages/lead-retrieval/LeadScannerPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyLeadsListPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "leads-list" */ "../pages/lead-retrieval/LeadsListPage"
      ),
  ),
  "lazyLoad.events",
);

export const LazyLeadKioskPage = withLazyLoading(
  React.lazy(
    () =>
      import(
        /* webpackChunkName: "lead-kiosk" */ "../pages/lead-retrieval/KioskPage"
      ),
  ),
  "lazyLoad.events",
);
